import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { FaPencilRuler } from '@react-icons/all-files/fa/FaPencilRuler';
import { FaProjectDiagram } from '@react-icons/all-files/fa/FaProjectDiagram';
import { FaCog } from '@react-icons/all-files/fa/FaCog';
import { FaHome } from '@react-icons/all-files/fa/FaHome';
import { FaCheck } from '@react-icons/all-files/fa/FaCheck';
import { FaTimes } from '@react-icons/all-files/fa/FaTimes';
import { FaExclamationTriangle } from '@react-icons/all-files/fa/FaExclamationTriangle';
import Breadcrumb from '../../../../commons/Breadcrumb';
import PageHeader from '../../../../commons/PageHeader';
import PageAlert, { usePageAlert } from '../../../../commons/PageAlert';
import ProfaseDataGrid from '../../../../commons/Datagrid';
import { DesignButton, RenderActions } from '../../../../commons/DataGridActions';
import { useAppDispatch, useAppSelector } from '../../../../store';
import { getEntities } from '../../../../manager/workflow-empresa-diseno.reducer';

export default function WorkflowEmpresaDisenoListPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const topRef = useRef<HTMLDivElement>(null);

  const entities = useAppSelector(s => s.workflowEmpresaDiseno.entities);
  const totalItems = useAppSelector(s => s.workflowEmpresaDiseno.totalItems);
  const loading = useAppSelector(s => s.workflowEmpresaDiseno.loading);

  const [rows, setRows] = useState<any[]>([]);

  const { alert, showToast } = usePageAlert();

  useEffect(() => {
    const msg = (location.state as any)?.successMsg;
    if (msg) { showToast('success', msg); window.history.replaceState({}, ''); }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { setRows(entities ?? []); }, [entities]);

  const fetchData = (params: any) => {
    dispatch(getEntities(params));
  };

  const cols = [
    {
      field: 'id',
      headerName: 'ID',
      flex: 1,
      renderCell: (params: any) =>
        params.row.id ?? '—',
    },
    {
      field: 'rfc',
      headerName: 'RFC',
      flex: 1,
      renderCell: (params: any) =>
        params.row.profaseWorkflowEmpresa?.empresa?.rfc ?? '—',
    },
    {
      field: 'razonSocial',
      headerName: 'Razón Social',
      flex: 2,
      renderCell: (params: any) =>
        params.row.profaseWorkflowEmpresa?.empresa?.razonSocial ?? '—',
    },
    {
      field: 'workflow',
      headerName: 'Workflow',
      flex: 1.5,
      renderCell: (params: any) =>
        params.row.profaseWorkflowEmpresa?.profaseWorkflow?.nombre
        ?? params.row.profaseWorkflowEmpresa?.workflow?.nombre
        ?? '—',
    },
    {
      field: 'activo',
      headerName: 'Activo',
      flex: 0.6,
      renderCell: (params: any) =>
        params.row.activo
          ? <FaCheck color="#22c55e" />
          : <FaTimes color="#ef4444" />,
    },
    {
      field: 'requiereAtencion',
      headerName: 'Requiere Atención',
      flex: 0.9,
      renderCell: (params: any) =>
        params.row.requiereAtencion
          ? (
            <span style={{ display: 'flex', alignItems: 'center', gap: 5, color: '#f59e0b', fontWeight: 600, fontSize: 13 }}>
              <FaExclamationTriangle />
              Sí
            </span>
          )
          : <span style={{ display: 'flex', alignItems: 'center', gap: 5, color: '#dddddd', fontWeight: 600, fontSize: 13 }}>
            <FaExclamationTriangle />
            No
          </span>,
    },
    {
      field: '__actions',
      headerName: '',
      flex: 0.5,
      renderCell: (params: any) => {
        const weId = params.row.profaseWorkflowEmpresa?.id;
        if (!weId) return null;
        return (
          <RenderActions>
            <DesignButton
              title="Ir al diseño"
              onClick={() => navigate(`/configuracion/workflow/workflow-empresa/${weId}/diseno`)}
            />
          </RenderActions>
        );
      },
    },
  ];

  const filters = [
    { label: 'RFC', name: 'empresaRfc' },
    { label: 'Razón Social', name: 'empresaRazonSocial' },
    { label: 'Workflow', name: 'workflowNombre' },
    { label: 'Requiere Atención', name: 'requiereAtencion' },
  ];

  return (
    <div style={g.page} ref={topRef}>
      <Breadcrumb items={[
        { label: 'Inicio', to: '/', icon: <FaHome /> },
        { label: 'Configuración', to: '/configuracion', icon: <FaCog /> },
        { label: 'Workflow', to: '/configuracion/workflow', icon: <FaProjectDiagram /> },
        { label: 'Workflow Empresa Diseño', icon: <FaPencilRuler /> },
      ]} />

      <PageHeader
        icon={<FaPencilRuler />}
        iconColor="#f97316"
        title="Workflow Empresa Diseño"
        subtitle="Diseños de workflow por empresa"
      />

      {alert && <PageAlert alert={alert} />}

      <ProfaseDataGrid
        cols={cols}
        title=""
        filters={filters}
        fetch={fetchData}
        rowCount={totalItems}
        loading={loading}
        rows={rows}
      />
    </div>
  );
}

const g: Record<string, React.CSSProperties> = {
  page: { display: 'flex', flexDirection: 'column', gap: 24 },
};
